import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, BarChart, CheckCircle, XCircle, AlertCircle, RefreshCw, Download, Eye, Clock, MessageCircle, Send, CheckCheck, Phone, User, Search, Filter, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import Header from '@/components/Header';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const WhatsAppReports = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [deliveryReport, setDeliveryReport] = useState(null);
  const [filter, setFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [days, setDays] = useState('7');
  const [sortColumn, setSortColumn] = useState('date_sent');
  const [sortDirection, setSortDirection] = useState('desc');

  useEffect(() => {
    fetchDeliveryReport();
  }, [days]);

  const fetchDeliveryReport = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/whatsapp/delivery-report?days=${days}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDeliveryReport(response.data);
      toast.success(`Fetched ${response.data.total} messages`);
    } catch (error) {
      console.error('Error fetching delivery report:', error);
      toast.error('Failed to fetch delivery report: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const getSortIcon = (column) => {
    if (sortColumn !== column) {
      return <ArrowUpDown size={14} className="ml-1 text-gray-400" />;
    }
    return sortDirection === 'asc' 
      ? <ArrowUp size={14} className="ml-1 text-blue-600" />
      : <ArrowDown size={14} className="ml-1 text-blue-600" />;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'read':
        return <CheckCheck className="text-blue-500" size={16} />;
      case 'delivered':
        return <CheckCircle className="text-green-500" size={16} />;
      case 'sent':
        return <Send className="text-blue-400" size={16} />;
      case 'queued':
      case 'accepted':
        return <Clock className="text-yellow-500" size={16} />;
      case 'failed':
        return <XCircle className="text-red-500" size={16} />;
      case 'undelivered':
        return <AlertCircle className="text-orange-500" size={16} />;
      default:
        return <AlertCircle className="text-gray-500" size={16} />;
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      read: 'bg-blue-100 text-blue-700',
      delivered: 'bg-green-100 text-green-700',
      sent: 'bg-blue-50 text-blue-600',
      queued: 'bg-yellow-100 text-yellow-700',
      accepted: 'bg-yellow-100 text-yellow-700',
      failed: 'bg-red-100 text-red-700',
      undelivered: 'bg-orange-100 text-orange-700'
    };
    return styles[status] || 'bg-gray-100 text-gray-700';
  };

  const filteredMessages = deliveryReport?.messages?.filter(msg => {
    // Filter by status
    if (filter !== 'all' && msg.status !== filter) return false;
    
    // Filter by search
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        msg.to?.toLowerCase().includes(query) ||
        msg.member_name?.toLowerCase().includes(query) ||
        msg.member_id?.toLowerCase().includes(query)
      );
    }
    return true;
  }) || [];

  // Sort filtered messages
  const sortedMessages = [...filteredMessages].sort((a, b) => {
    let aVal, bVal;
    
    switch (sortColumn) {
      case 'status':
        aVal = a.status || '';
        bVal = b.status || '';
        break;
      case 'date_sent':
        aVal = a.date_sent ? new Date(a.date_sent).getTime() : 0;
        bVal = b.date_sent ? new Date(b.date_sent).getTime() : 0;
        break;
      case 'date_updated':
        aVal = a.date_updated ? new Date(a.date_updated).getTime() : 0;
        bVal = b.date_updated ? new Date(b.date_updated).getTime() : 0;
        break;
      case 'member_id':
        aVal = a.member_id || 'zzz'; // Put empty values at end
        bVal = b.member_id || 'zzz';
        break;
      default:
        aVal = a[sortColumn] || '';
        bVal = b[sortColumn] || '';
    }
    
    if (typeof aVal === 'string') {
      aVal = aVal.toLowerCase();
      bVal = bVal.toLowerCase();
    }
    
    if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  const exportReport = () => {
    if (!sortedMessages.length) {
      toast.info('No messages to export');
      return;
    }
    
    const csv = [
      'Member/Advertiser ID,Type,Name,Database,Phone,Status,Error Code,Error Message,Date Sent,Date Updated',
      ...sortedMessages.map(m => 
        `"${m.member_id || ''}","${m.entity_type || ''}","${m.member_name || ''}","${m.database || ''}","${m.to}","${m.status}","${m.error_code || ''}","${m.error_message || ''}","${m.date_sent || ''}","${m.date_updated || ''}"`
      )
    ].join('\n');
    
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `WhatsApp_Delivery_Report_${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    
    toast.success(`Exported ${sortedMessages.length} records`);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <div className="max-w-7xl mx-auto p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center">
            <Button variant="ghost" onClick={() => navigate('/')} className="mr-4">
              <ArrowLeft size={20} />
            </Button>
            <div>
              <h1 className="text-2xl font-bold flex items-center">
                <BarChart className="mr-3 text-green-600" size={28} />
                WhatsApp Delivery Report
              </h1>
              <p className="text-gray-600 mt-1">View message delivery status from Twilio</p>
            </div>
          </div>
          
          <div className="flex items-center space-x-3">
            <Select value={days} onValueChange={setDays}>
              <SelectTrigger className="w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="1">Last 1 day</SelectItem>
                <SelectItem value="3">Last 3 days</SelectItem>
                <SelectItem value="7">Last 7 days</SelectItem>
                <SelectItem value="14">Last 14 days</SelectItem>
                <SelectItem value="30">Last 30 days</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={fetchDeliveryReport} disabled={loading}>
              <RefreshCw size={18} className={`mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>

        {/* Summary Cards */}
        {deliveryReport && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4 mb-6">
            <div className="bg-white rounded-lg shadow-sm border p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500">Total</p>
                  <p className="text-xl font-bold">{deliveryReport.total}</p>
                </div>
                <MessageCircle className="text-gray-400" size={24} />
              </div>
            </div>
            <div className="bg-white rounded-lg shadow-sm border p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500">Read</p>
                  <p className="text-xl font-bold text-blue-600">{deliveryReport.read}</p>
                </div>
                <CheckCheck className="text-blue-400" size={24} />
              </div>
            </div>
            <div className="bg-white rounded-lg shadow-sm border p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500">Delivered</p>
                  <p className="text-xl font-bold text-green-600">{deliveryReport.delivered}</p>
                </div>
                <CheckCircle className="text-green-400" size={24} />
              </div>
            </div>
            <div className="bg-white rounded-lg shadow-sm border p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500">Sent</p>
                  <p className="text-xl font-bold text-blue-500">{deliveryReport.sent}</p>
                </div>
                <Send className="text-blue-300" size={24} />
              </div>
            </div>
            <div className="bg-white rounded-lg shadow-sm border p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500">Queued</p>
                  <p className="text-xl font-bold text-yellow-600">{deliveryReport.queued}</p>
                </div>
                <Clock className="text-yellow-400" size={24} />
              </div>
            </div>
            <div className="bg-white rounded-lg shadow-sm border p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500">Failed</p>
                  <p className="text-xl font-bold text-red-600">{deliveryReport.failed}</p>
                </div>
                <XCircle className="text-red-400" size={24} />
              </div>
            </div>
            <div className="bg-white rounded-lg shadow-sm border p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500">Undelivered</p>
                  <p className="text-xl font-bold text-orange-600">{deliveryReport.undelivered}</p>
                </div>
                <AlertCircle className="text-orange-400" size={24} />
              </div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm border p-4 mb-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center space-x-2 flex-1 min-w-[200px]">
              <Search size={18} className="text-gray-400" />
              <Input 
                placeholder="Search by phone, name, or ID..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="max-w-sm"
              />
            </div>
            
            <div className="flex items-center space-x-2">
              <Filter size={18} className="text-gray-400" />
              <Select value={filter} onValueChange={setFilter}>
                <SelectTrigger className="w-40">
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="read">Read</SelectItem>
                  <SelectItem value="delivered">Delivered</SelectItem>
                  <SelectItem value="sent">Sent</SelectItem>
                  <SelectItem value="queued">Queued</SelectItem>
                  <SelectItem value="failed">Failed</SelectItem>
                  <SelectItem value="undelivered">Undelivered</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <Button variant="outline" onClick={exportReport}>
              <Download size={16} className="mr-2" />
              Export CSV
            </Button>
          </div>
          
          {searchQuery || filter !== 'all' ? (
            <p className="text-sm text-gray-500 mt-2">
              Showing {sortedMessages.length} of {deliveryReport?.total || 0} messages
            </p>
          ) : null}
        </div>

        {/* Messages Table */}
        <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th 
                    className="text-left p-3 text-sm font-medium text-gray-600 cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('status')}
                  >
                    <div className="flex items-center">
                      Status {getSortIcon('status')}
                    </div>
                  </th>
                  <th className="text-left p-3 text-sm font-medium text-gray-600">Phone</th>
                  <th 
                    className="text-left p-3 text-sm font-medium text-gray-600 cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('member_id')}
                  >
                    <div className="flex items-center">
                      Member/Advertiser {getSortIcon('member_id')}
                    </div>
                  </th>
                  <th 
                    className="text-left p-3 text-sm font-medium text-gray-600 cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('date_sent')}
                  >
                    <div className="flex items-center">
                      Date Sent {getSortIcon('date_sent')}
                    </div>
                  </th>
                  <th 
                    className="text-left p-3 text-sm font-medium text-gray-600 cursor-pointer hover:bg-gray-100 select-none"
                    onClick={() => handleSort('date_updated')}
                  >
                    <div className="flex items-center">
                      Last Updated {getSortIcon('date_updated')}
                    </div>
                  </th>
                  <th className="text-left p-3 text-sm font-medium text-gray-600">Error</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {loading ? (
                  <tr>
                    <td colSpan={6} className="p-8 text-center text-gray-500">
                      <RefreshCw className="animate-spin mx-auto mb-2" size={32} />
                      Fetching delivery report from Twilio...
                    </td>
                  </tr>
                ) : sortedMessages.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="p-8 text-center text-gray-500">
                      <MessageCircle className="mx-auto mb-2 opacity-50" size={48} />
                      <p>No messages found</p>
                      <p className="text-sm mt-1">Try adjusting your filters or date range</p>
                    </td>
                  </tr>
                ) : (
                  sortedMessages.map((msg, index) => (
                    <tr key={msg.sid || index} className="hover:bg-gray-50">
                      <td className="p-3">
                        <div className="flex items-center space-x-2">
                          {getStatusIcon(msg.status)}
                          <span className={`text-xs px-2 py-1 rounded-full ${getStatusBadge(msg.status)}`}>
                            {msg.status}
                          </span>
                        </div>
                      </td>
                      <td className="p-3">
                        <div className="flex items-center space-x-2">
                          <Phone size={14} className="text-gray-400" />
                          <span className="font-mono text-sm">{msg.to}</span>
                        </div>
                      </td>
                      <td className="p-3">
                        {msg.member_id ? (
                          <div>
                            <span className={`font-medium ${msg.entity_type === 'Advertiser' ? 'text-green-600' : 'text-blue-600'}`}>
                              {msg.member_id}
                            </span>
                            {msg.entity_type && (
                              <span className="text-xs text-gray-400 ml-1">({msg.entity_type})</span>
                            )}
                            {msg.member_name && (
                              <div className="text-gray-600 text-sm">{msg.member_name}</div>
                            )}
                            {msg.database && (
                              <div className="text-xs text-gray-400">{msg.database}</div>
                            )}
                          </div>
                        ) : (
                          <span className="text-gray-400 text-sm">Not in database</span>
                        )}
                      </td>
                      <td className="p-3 text-sm text-gray-600">
                        {formatDate(msg.date_sent)}
                      </td>
                      <td className="p-3 text-sm text-gray-600">
                        {formatDate(msg.date_updated)}
                      </td>
                      <td className="p-3">
                        {msg.error_code || msg.error_message ? (
                          <div className="text-xs">
                            {msg.error_code && (
                              <span className="bg-red-100 text-red-700 px-2 py-0.5 rounded mr-2">
                                {msg.error_code}
                              </span>
                            )}
                            <span className="text-red-600">{msg.error_message}</span>
                          </div>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Info Note */}
        <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
          <p className="text-sm text-blue-700">
            <strong>Note:</strong> This report fetches live data directly from Twilio. Status meanings:
          </p>
          <ul className="text-sm text-blue-600 mt-2 grid grid-cols-2 md:grid-cols-3 gap-2">
            <li className="flex items-center"><CheckCheck size={14} className="mr-2" /> <strong>Read</strong> - Message opened</li>
            <li className="flex items-center"><CheckCircle size={14} className="mr-2" /> <strong>Delivered</strong> - Reached phone</li>
            <li className="flex items-center"><Send size={14} className="mr-2" /> <strong>Sent</strong> - Sent to WhatsApp</li>
            <li className="flex items-center"><Clock size={14} className="mr-2" /> <strong>Queued</strong> - Waiting to send</li>
            <li className="flex items-center"><XCircle size={14} className="mr-2" /> <strong>Failed</strong> - Could not send</li>
            <li className="flex items-center"><AlertCircle size={14} className="mr-2" /> <strong>Undelivered</strong> - Not on WhatsApp</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default WhatsAppReports;
